import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { LOGO_ICON } from "@/lib/data";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Know Your Heart", id: "know-your-heart", route: "/know-your-heart" },
  { label: "Community Screenings", id: "screenings" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goRoute = (route) => {
    setOpen(false);
    navigate(route);
    window.scrollTo(0, 0);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-border transition-shadow duration-300 ${scrolled ? "shadow-md" : ""}`}
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 h-24 flex items-center justify-between gap-4">
        <button data-testid="logo-home-button" onClick={() => go("home")} className="flex items-center gap-3 shrink-0">
          <img src={LOGO_ICON} alt="Cardiac Precision Diagnostics" className="h-20 w-20 object-contain" />
          <span className="hidden sm:flex flex-col leading-tight text-left whitespace-nowrap">
            <span className="font-heading text-lg text-secondary font-semibold">Cardiac Precision</span>
            <span className="text-[11px] tracking-[0.22em] uppercase text-primary font-bold">Diagnostics, LLC</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-x-6 xl:gap-x-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => (l.route ? goRoute(l.route) : go(l.id))}
              className="text-sm font-semibold text-secondary/80 hover:text-primary transition-colors duration-200 whitespace-nowrap"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <a
            data-testid="nav-phone"
            href="tel:3465384241"
            className="hidden xl:flex items-center gap-2 text-sm font-semibold text-secondary hover:text-primary transition-colors duration-200 whitespace-nowrap"
          >
            <Phone size={16} /> 346-538-4241
          </a>
          <button
            data-testid="nav-request-button"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white hover:bg-secondary transition-colors duration-200"
          >
            Request Service
          </button>
          <button data-testid="mobile-menu-toggle" className="lg:hidden text-secondary" onClick={() => setOpen(!open)}>
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-border px-5 py-4 flex flex-col gap-1">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => (l.route ? goRoute(l.route) : go(l.id))}
              className="text-left py-3 text-base font-semibold text-secondary/90 hover:text-primary border-b border-border/60"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="mobile-request-button"
            onClick={() => go("contact")}
            className="mt-3 rounded-full bg-primary px-5 py-3 text-sm font-bold text-white"
          >
            Request Service
          </button>
        </div>
      )}
    </header>
  );
}
